import { Button, Group } from "@mantine/core";
import { IEditorMetadata } from "@mctypes/global.types";
import { IProjectMemberApiItem } from "@schemas/projectSchema";
import { IconSend, IconX } from "@tabler/icons-react";
import { EditorState } from "lexical";
import React, { useEffect, useState } from "react";
import RichTextEditor, {
  lexicalStateToPlainText,
  plainTextToLexicalState,
} from "../RichTextEditorMantine/RichTextEditor";

const EditComment: React.FC<{
  onClose: () => void;
  text: string;
  onSave: (text: string, metadata: IEditorMetadata) => void | Promise<void>;
  members: IProjectMemberApiItem[];
}> = ({ onClose, text, onSave, members }) => {
  // Each mounted edit session owns its initial content, even if the parent refreshes.
  const [originalText] = useState(text);
  const [initialContent, setInitialContent] = useState<string | null>(null);
  const [editorState, setEditorState] = useState<EditorState | null>(null);
  const [mentionIds, setMentionIds] = useState<string[]>([]);
  const [plainText, setPlainText] = useState("");
  const [hasImages, setHasImages] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    plainTextToLexicalState(originalText).then(
      (content) => {
        if (!cancelled) setInitialContent(content);
      },
      () => {
        if (!cancelled) setSaveError("Kunne ikke åpne kommentaren for redigering.");
      },
    );
    return () => {
      cancelled = true;
    };
  }, [originalText]);

  const save = async () => {
    // OnChange need not fire when an existing comment is opened and saved unchanged.
    if (editorState && JSON.stringify(editorState) !== initialContent) {
      const nextPlainText = await lexicalStateToPlainText(editorState);
      await onSave(JSON.stringify(editorState), { mentionIdArray: mentionIds, plainText: nextPlainText });
    }
    onClose();
  };

  return (
    <div style={{ width: "100%" }}>
      {initialContent !== null && (
        <RichTextEditor
          richTextTree={initialContent}
          handleMentions={setMentionIds}
          onChange={(nextState, nextPlainText, nextHasImages) => {
            setEditorState(nextState);
            setPlainText(nextPlainText);
            setHasImages(nextHasImages);
          }}
          members={members}
        />
      )}

      {saveError && <div role="alert">{saveError}</div>}
      <Group mt="xs" gap="xs" justify="flex-end">
        <Button
          loading={saving}
          disabled={saving || initialContent === null || (editorState !== null && !plainText.trim() && !hasImages)}
          leftSection={<IconSend size={16} />}
          variant="filled"
          size="sm"
          onClick={async () => {
            setSaving(true);
            setSaveError(null);
            try {
              await save();
            } catch {
              setSaveError("Kunne ikke lagre kommentaren. Teksten er beholdt.");
            } finally {
              setSaving(false);
            }
          }}
        >
          Lagre
        </Button>

        <Button disabled={saving} onClick={onClose} aria-label="Avbryt redigering" size="sm" variant="default">
          <IconX size={16} />
        </Button>
      </Group>
    </div>
  );
};

export default EditComment;
